import type { ChatResponse } from '@/types';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { updateChatInLocalStorage } from '@/hooks/useMigrateChatsFromLocalStorage';

export const GET_CHAT_BY_ID = 'get_chat_by_id';

/**
 * Validates that chatId is a valid MongoDB ObjectId format (24 hex characters).
 * This prevents SSRF attacks by ensuring only safe identifiers are used in URLs.
 */
function isValidChatId(chatId: string | null | undefined): boolean {
  if (!chatId || typeof chatId !== 'string') {
    return false;
  }
  return /^[a-fA-F0-9]{24}$/.test(chatId);
}

export function useQueryChatById(chatId: string | null | undefined) {
  const isValid = isValidChatId(chatId);
  const { isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();

  const result = useQuery<ChatResponse>({
    queryKey: [GET_CHAT_BY_ID, chatId, isValid],
    enabled: !!chatId && isValid,
    queryFn: async () => {
      if (!isValid) {
        throw new Error('Invalid chatId format');
      }
      const { data } = await scribeHttpV2.get<ChatResponse>(`/chat/chats/${chatId}`);
      return data;
    },
  });

  // Persist a copy to localStorage only when user is not authenticated (for later migration).
  // Authenticated users are not written so localStorage does not grow indefinitely.
  useEffect(() => {
    if (!chatId || !result.data || isAuthenticated || isAuthLoading) return;
    updateChatInLocalStorage(chatId, result.data);
  }, [chatId, result.data, isAuthenticated, isAuthLoading]);

  return result;
}
